import {
	BarController,
	BarElement,
	type BarControllerDatasetOptions,
} from 'chart.js'
import 'chartjs-adapter-date-fns'

// export interface ChevronStep {
// 	name: string
// 	endDate: Date
// 	/**
// 	 * Required on the first step of a chevron. On later steps, omitting it
// 	 * chains this step's start to the previous step's endDate.
// 	 */
// 	startDate?: Date
// 	/** Overrides the chevron's color for this step only. */
// 	color?: string
// 	/** true (default) = angled point at the end of this step. false = flat vertical line. */
// 	openEnded?: boolean
// 	/** true (default) = angled point at the start of this step. false = flat vertical line. */
// 	openStarted?: boolean
// }

// export interface Chevron {
// 	steps: ChevronStep[]
// 	/** Default color for every step in this chevron, unless a step overrides it. */
// 	color?: string
// 	/** Optional row label, defaults to "Row N". */
// 	label?: string
// 	milestones?: { name: string; date: Date }[]
// }

export interface ChevronStepDataPoint {
	x: [Date, Date]
	y: string
	name: string
	color: string
	/** Set by buildChevronChartConfig based on the previous step's openEnded flag. */
	startOpen?: boolean
	/** Mirrors ChevronStep.openEnded (default true). */
	endOpen?: boolean
}

export interface ChevronControllerDatasetOptions extends BarControllerDatasetOptions {
	/** Fixed depth (px) of the angled point/notch. Constant regardless of step width — this is what keeps the angle consistent. */
	pointDepth?: number
	/** Fraction (0–1) of pointDepth that open edges overhang into the next step, to narrow the visual gap. Default 1/3. */
	overhangRatio?: number
}

declare module 'chart.js' {
	interface ChartTypeRegistry {
		chevron: {
			chartOptions: ChartTypeRegistry['bar']['chartOptions']
			datasetOptions: ChevronControllerDatasetOptions
			defaultDataPoint: ChevronStepDataPoint
			metaExtensions: ChartTypeRegistry['bar']['metaExtensions']
			parsedDataType: ChartTypeRegistry['bar']['parsedDataType']
			scales: ChartTypeRegistry['bar']['scales']
		}
	}
}

// ---------------------------------------------------------------------
// 1. Custom element: draws a chevron/arrow shape instead of a rectangle
// ---------------------------------------------------------------------
export class ChevronElement extends BarElement {
	draw(ctx) {
		const { x, y, base, height } = this.getProps(
			['x', 'y', 'base', 'height'],
			true,
		)
		const { startOpen, endOpen, pointDepth, overhangRatio } = this
			.options as any

		const left = Math.min(base, x)
		const right = Math.max(base, x)
		const top = y - height / 2
		const bottom = y + height / 2

		// Fixed depth (pixels), NOT derived from bar width — this is what keeps
		// the angle constant regardless of step length. Clamped only as a
		// safety net for pathologically short steps.
		const rawNotch = pointDepth ?? height / 2
		const ratio = overhangRatio ?? 0
		// The real constraint: the two angled tips must not cross each other.
		// Solving lTipX <= rTipX for notch gives this bound — much looser than
		// a flat "half the bar width," so it only kicks in on genuinely
		// too-short steps instead of every moderately short one.
		const maxNotch = ratio < 0.5 ? (right - left) / (1 - 2 * ratio) : Infinity
		const notch = Math.max(0, Math.min(rawNotch, maxNotch))
		const overhang = notch * (overhangRatio ?? 0)

		const lTop = startOpen ? [left - overhang, top] : [left, top]
		const lBottom = startOpen ? [left - overhang, bottom] : [left, bottom]
		const lTip = startOpen ? [left + notch - overhang, y] : null

		const rTop = endOpen ? [right - notch + overhang, top] : [right, top]
		const rBottom = endOpen
			? [right - notch + overhang, bottom]
			: [right, bottom]
		const rTip = endOpen ? [right + overhang, y] : null

		ctx.save()
		ctx.beginPath()
		ctx.moveTo(...lTop)
		ctx.lineTo(...rTop)
		if (rTip) ctx.lineTo(...rTip)
		ctx.lineTo(...rBottom)
		ctx.lineTo(...lBottom)
		if (lTip) ctx.lineTo(...lTip)
		ctx.closePath()

		ctx.fillStyle = this.options.backgroundColor
		ctx.fill()
		if (this.options.borderWidth) {
			ctx.lineWidth = this.options.borderWidth
			ctx.strokeStyle = this.options.borderColor
			ctx.stroke()
		}
		ctx.restore()
	}
}
ChevronElement.id = 'chevronElement'
ChevronElement.defaults = {
	...BarElement.defaults,
	startOpen: true,
	endOpen: true,
	pointDepth: undefined, // resolved per-dataset; falls back to height/2 in draw()
	overhangRatio: 1 / 3,
}
// ---------------------------------------------------------------------
// 2. Custom controller: reuses BarController's floating-bar data
//    handling, just swaps in the chevron element for drawing.
// ---------------------------------------------------------------------
export class ChevronController extends BarController {
	updateElements(elements, start, count, mode) {
		super.updateElements(elements, start, count, mode)
	}
}
ChevronController.id = 'chevron'
ChevronController.defaults = {
	...BarController.defaults,
	dataElementType: 'chevronElement',
}
